/* eslint-disable radix */
/* eslint-disable consistent-return */
import _ from 'lodash';

import { buildAd } from './CampaignBuilder';
import constants from './constants/constants';
import { getToken } from '../../services/TokenService';
import {
  createEventSourceGroup,
  shareEventSourceGroupInAccount
} from '../../fbApi/EventSourceGroup';
import { buildAudience } from '../AudienceBuilder/AudienceCreator';
import { getCanvas } from '../../services/CanvasService';
import {
  launchCanvasToFacebook,
  formatCanvasToLaunch
} from '../Canvas/CanvasLauncher';
import { getCanvas as getCanvasFromFacebook } from '../../fbApi/Canvas';
import { createCollectionAd as createCollection } from '../AdBuilder/AdCreator';
import { createCreative } from '../../fbApi/Creative';
import { returnConversionDomainUrl } from '../../utils/urls';

// Method to create a video Ad
export const createVideoAd = async (
  ad,
  campaign,
  newAdSetId,
  adset,
  token,
  form
) => {
  const adData = await createVideoAdObject(
    ad,
    campaign,
    newAdSetId,
    adset,
    form
  );

  let resultAd = '';
  resultAd = await buildAd(adData, token);

  let attempts = 0;

  while (attempts < 5 && resultAd.error) {
    attempts = attempts + 1;
    // eslint-disable-next-line no-await-in-loop
    resultAd = await buildAd(adData, token);
  }
  resultAd.name = adData.name;

  return resultAd;
};

// Method to retrieve the video object to launch video Ad
const createVideoAdObject = async (ad, campaign, newAdSetId, adset, form) => {
  const {
    accountId,
    pageId,
    displayUrl,
    ads,
    accountName,
    pixel,
    status,
    utmProfile,
    footerUrl
  } = form;
  const { ad: innerAd, name } = ad;
  const {
    asset_id: videoId,
    title,
    message,
    call_to_action: cta,
    footer_text: footerText,
    type
  } = innerAd;
  const isCollection = type === constants.LAUNCHER.COLLECTION;

  let newDisplayUrl = displayUrl || footerUrl;
  let destinationUrl = '';
  let adName = name;
  let adFields = ads.find((item) => item.id === parseInt(ad.id, 10));
  if (adFields) {
    destinationUrl = adFields.destinationUrl;
  }

  if (isCollection) {
    adName = adName.replace('Collection', 'Cinemagraph');
    newDisplayUrl = newDisplayUrl.replace(/(http|https):\/\//g, '');
    if (newDisplayUrl.charAt(newDisplayUrl.length - 1) === '/') {
      newDisplayUrl = newDisplayUrl.substr(0, newDisplayUrl.length - 1);
    }
  }

  if (!adFields) {
    const { duplicateType } = ad;
    adFields = ads.find((item) => {
      switch (duplicateType) {
        case 'lookalike':
          return ad.id === item.id * 2 + 1;
        case 'retargeting':
          return ad.id === item.id * 2;
        default:
          return ad.id === item.id;
      }
    });
  }

  if (adFields) {
    destinationUrl = adFields.destinationUrl || adFields.footerUrl;
  }

  const videoAdObject = {
    adSetId: newAdSetId,
    accountId,
    status,
    name: adName.replace(constants.LAUNCHER.CLIENT_NAME, accountName),
    creative: {
      object_story_spec: {
        page_id: pageId,
        video_data: {
          link_description: isCollection ? ' ' : footerText,
          video_id: parseInt(videoId, 10),
          title,
          message,
          call_to_action: {
            type: isCollection ? 'SHOP_NOW' : cta,
            value: {
              link_caption: isCollection ? newDisplayUrl : displayUrl,
              link: isCollection && footerUrl ? footerUrl : destinationUrl
            }
          },
          image_url: ad.ad.thumbnail
        }
      },
      degrees_of_freedom_spec: {
        creative_features_spec: {
          standard_enhancements: {
            enroll_status: 'OPT_OUT'
          }
        }
      },
      url_tags: utmProfile && utmProfile.video ? utmProfile.video : ad.ad.utm
    },
    tracking_specs: [
      {
        'action.type': [constants.LAUNCHER.OFFSITE_CONVERSIONS_VALUE],
        fb_pixel: [pixel]
      }
    ],
    conversion_domain: returnConversionDomainUrl({ destinationUrl, campaign })
  };

  return videoAdObject;
};

// Initialize the positions according the type for the adset
export const setPositions = (adSetData, positions) => {
  const adSet = adSetData;

  positions.forEach((position) => {
    switch (true) {
      case position === constants.LAUNCHER.FACEBOOK:
        adSet.targeting.facebook_positions = [
          'feed',
          'right_hand_column',
          'instant_article',
          'marketplace',
          'video_feeds',
          'story',
          'search'
        ];
        break;
      case position === constants.LAUNCHER.INSTAGRAM:
        adSet.targeting.instagram_positions = ['stream', 'story', 'explore'];
        break;
      case position === constants.LAUNCHER.AUDIENCENETWORK:
        adSet.targeting.audience_network_positions = [
          'classic',
          'instream_video',
          'rewarded_video'
        ];
        break;
      case position === constants.LAUNCHER.MESSENGER:
        adSet.targeting.messenger_positions = ['messenger_home', 'story'];
        break;
      default:
        break;
    }
  });
};

// Method to validate the form values for campaigns, adset and ads to launch the package
export const validFields = (
  form,
  zipCodesToInclude,
  checkedAdIds,
  packageToLaunch,
  packageContainsHecCampaign
) => {
  const {
    accountId,
    pageId,
    startDate,
    endDate,
    address,
    radius,
    campaigns,
    adsets,
    displayUrl,
    ads
  } = form;

  if (
    !accountId ||
    !pageId ||
    !startDate ||
    !endDate ||
    (!address && (!zipCodesToInclude || zipCodesToInclude.length === 0)) ||
    !radius ||
    !displayUrl
  ) {
    return false;
  }

  let validCampaigns = true;
  const radiusFloat = parseFloat(radius);

  if (
    (isNaN(radiusFloat) || radiusFloat < 20.0) &&
    packageContainsHecCampaign
  ) {
    return false;
  }

  packageToLaunch.campaigns.forEach((campaign) => {
    const campaignParameters = campaigns.find(
      (item) => item.id === campaign.id
    );

    if (
      !campaignParameters ||
      !campaignParameters.spendCap ||
      (campaign.objective === constants.LAUNCHER.PRODUCT_CATALOG_SALES &&
        !campaignParameters.productCatalog)
    ) {
      validCampaigns = false;
    }
  });

  packageToLaunch.adsets.forEach((adset) => {
    const adsetParameters = adsets.find((item) => item.id === adset.id);

    if (
      adset.daaType &&
      (!adsetParameters || (adsetParameters && !adsetParameters.productSet))
    ) {
      validCampaigns = false;
    }
  });

  packageToLaunch.ads.forEach((ad) => {
    const adParameters = ads.find((item) => item.id === ad.id);

    if (
      ad.type === constants.LAUNCHER.COLLECTION &&
      checkedAdIds.indexOf(ad.id) !== -1 &&
      (!adParameters ||
        !adParameters.productCatalog ||
        !adParameters.productSet)
    ) {
      validCampaigns = false;
    }
  });

  return validCampaigns;
};

// Format the selected dates to launch adsets. It creates the Event Source Group for the audience.
export const formatDates = (date, time) => {
  return `${date.toISOString().split('T')[0]}T${time}:00-0400`;
};

// Method to create an Audience for DAA Adset
export const createDAAAudience = async (productSet, form, token) => {
  const { accountId, pixel } = form;
  const resultToken = await getToken('alexia_user_token');
  const alexiaToken = resultToken.token.token;

  const data = {
    name: 'Event Source Group',
    pixel
  };

  const resultEvent = await createEventSourceGroup(data, alexiaToken);

  const shareData = {
    accounts: [accountId],
    eventSourceGroup: resultEvent.id
  };

  await shareEventSourceGroupInAccount(shareData, alexiaToken);

  const parameters = {
    pixel_id: pixel,
    name: 'DAA Audience',
    retention_days: 30,
    accountId,
    subtype: 'CLAIM',
    rule: { vehicle_set_id: { eq: productSet } },
    claim_objective: constants.LAUNCHER.TRAVEL,
    content_type: constants.LAUNCHER.VEHICLE,
    event_sources: [{ id: form.pixel, type: 'pixel', context_id: null }],
    inclusions: [
      {
        event: 'ViewContent',
        retention: { min_seconds: 0, max_seconds: 2592000 }
      }
    ],
    exclusions: []
  };

  const resultAudience = await buildAudience(parameters, token);
  return resultAudience;
};

// Method to create the object to launch DAA Adset to Facebook
export const createDAAAdsetObject = async (
  adset,
  campaign,
  newCampaign,
  form,
  parameters
) => {
  const {
    accountId,
    address,
    radius,
    startDate,
    endDate,
    accountName,
    adsets
  } = form;

  const adsetParameters = adsets.find((item) => item.id === adset.id);

  const startTime = formatDates(startDate, '9');
  const endTime = formatDates(endDate, '22');

  const customAudiences = [];
  if (adsetParameters && adsetParameters.audiences) {
    adsetParameters.audiences.forEach((audience) => {
      customAudiences.push({
        id: audience.value,
        name: audience.text
      });
    });
  }

  const exclusions = [];
  if (adsetParameters && adsetParameters.exclusions) {
    adsetParameters.exclusions.forEach((audience) => {
      exclusions.push({ id: audience.value, name: audience.text });
    });
  }

  /**
   * Define targeting object..
   */
  const targetingsentencelines = {
    id: '/targetingsentencelines'
  };
  let geoLocations = {};
  let excludedGeoLocations = {};

  if (
    parameters.locationType.toLowerCase() !== constants.LAUNCHER.ADDRESS_VALUE
  ) {
    const zipcodesInclude = convertZipcodeString(parameters.zipCodesToInclude);
    const zipcodesExclude = convertZipcodeString(parameters.zipCodesToExclude);

    geoLocations = {
      zips: zipcodesInclude.map((zip) => {
        return {
          key: `US:${zip}`
        };
      })
    };

    excludedGeoLocations = {
      zips: zipcodesExclude.map((zip) => {
        return {
          key: `US:${zip}`
        };
      })
    };
  } else {
    targetingsentencelines.targetingsentencelines = [
      {
        content: 'Location:',
        children: [`United States: ${address}`]
      }
    ];

    geoLocations = {
      custom_locations: [
        {
          name: address,
          address_string: address,
          radius,
          distance_unit: 'mile'
        }
      ],
      location_types: ['home', 'recent']
    };
  }

  const adSetData = {
    campaignId: newCampaign,
    accountId,
    name: adset.adSetName.replace(constants.LAUNCHER.CLIENT_NAME, accountName),
    start_time: startTime,
    end_time: endTime,
    status: constants.LAUNCHER.ACTIVE,
    productSet: adsetParameters.productSet,
    targeting: {},
    attribution_spec:
      adsetParameters.optimizationGoal === constants.LAUNCHER.LINK_CLICKS
        ? [{ event_type: constants.LAUNCHER.CLICK_THROUGH, window_days: 1 }]
        : [
            { event_type: constants.LAUNCHER.CLICK_THROUGH, window_days: 7 },
            { event_type: constants.LAUNCHER.VIEW_THROUGH, window_days: 1 }
          ],
    optimization_goal: adsetParameters.optimizationGoal
  };

  const audienceResult = await createDAAAudience(
    adsetParameters.productSet,
    form,
    parameters.token
  );

  if (adset.daaType === constants.LAUNCHER.RETARGETING) {
    if (audienceResult.id) {
      adSetData.targeting.dynamic_audience_ids = [audienceResult.id];
    }
  } else if (audienceResult.id) {
    adSetData.targeting.excluded_dynamic_audience_ids = [audienceResult.id];
  }

  if (adset.includeAudience) {
    adSetData.targeting.custom_audiences = customAudiences;
    adSetData.targeting.excluded_custom_audiences = exclusions;
  }

  if (adset.includeLocation) {
    adSetData.targeting.geo_locations = geoLocations;
    adSetData.targeting.excluded_geo_locations = excludedGeoLocations;
    adSetData.targetingsentencelines = targetingsentencelines;
  } else {
    adSetData.targeting.geo_locations = {
      countries: ['US'],
      location_types: ['home', 'recent']
    };
  }

  if (adset.includeTargeting) {
    const includeTargetingObject = parameters.includeTargetingSelected.find(
      (selection) => selection.adSetId === adset.id
    )
      ? parameters.includeTargetingSelected.find(
          (selection) => selection.adSetId === adset.id
        ).selected
      : [];
    const excludeTargetingObject = parameters.excludeTargetingSelected.find(
      (selection) => selection.adSetId === adset.id
    )
      ? parameters.excludeTargetingSelected.find(
          (selection) => selection.adSetId === adset.id
        ).selected
      : [];

    adSetData.targeting.flexible_spec = [
      {
        interests: includeTargetingObject
      }
    ];
    adSetData.targeting.exclusions = {
      interests: excludeTargetingObject
    };
  }

  adSetData.objective = constants.LAUNCHER.PRODUCT_CATALOG_SALES;
  adSetData.productSet = adsetParameters.productSet;

  return adSetData;
};

// The idea is that the user might insert zip codes string separated by several delemiters.
// - newline, comma, space
// split this string and map them into array of zip code string.
export const convertZipcodeString = (zipcodes) => {
  if (!zipcodes || zipcodes === '') return [];

  const delemiterRegex = / |,|\n/g;

  return zipcodes.split(delemiterRegex);
};

export const createConversionAdsetObject = (
  adset,
  campaign,
  newCampaign,
  form,
  parameters
) => {
  const {
    accountId,
    pixel,
    address,
    radius,
    startDate,
    endDate,
    displayUrl,
    accountName,
    adsets
  } = form;

  const { is_hec_campaign: isHecCampaign } = campaign;

  const adsetParameters = adsets.find((item) => item.id === adset.id);
  const positions = adset.positions.map((o) => o.value.toLowerCase());
  const devices = adset.devices.map((o) => o.value.toLowerCase());
  const languages = adset.languages.map((o) => o.value);
  const startTime = formatDates(startDate, '9');
  const endTime = formatDates(endDate, '22');

  const customAudiences = [];
  if (
    adsetParameters &&
    adsetParameters.audiences &&
    adsetParameters.audiences.length
  ) {
    adsetParameters.audiences.forEach((audience) => {
      if (!audience) {
        return;
      }
      customAudiences.push({
        id: audience.id || audience.value,
        name: audience.name || audience.text
      });
    });
  }

  const exclusions = [];
  if (adsetParameters && adsetParameters.exclusions) {
    adsetParameters.exclusions.forEach((audience) => {
      const { value: audienceValue, text: audienceText } = audience;
      if (audienceValue && audienceText) {
        exclusions.push({ id: audienceValue, name: audienceText });
      }
    });
  }

  const gender = adset.gender === 0 ? [1, 2] : [adset.gender];

  /**
   * Define targeting object..
   */
  const targetingsentencelines = {
    id: '/targetingsentencelines'
  };
  let geoLocations = {};
  let excludedGeoLocations = {};

  if (
    parameters.locationType.toLowerCase() !== constants.LAUNCHER.ADDRESS_VALUE
  ) {
    const zipcodesInclude = convertZipcodeString(parameters.zipCodesToInclude);
    const zipcodesExclude = convertZipcodeString(parameters.zipCodesToExclude);

    geoLocations = {
      zips: zipcodesInclude.map((zip) => {
        return {
          key: `US:${zip}`
        };
      })
    };

    excludedGeoLocations = {
      zips: zipcodesExclude.map((zip) => {
        return {
          key: `US:${zip}`
        };
      })
    };
  } else {
    targetingsentencelines.targetingsentencelines = isHecCampaign
      ? [
          {
            content: 'Location:',
            children: [`United States: ${address}`]
          }
        ]
      : [
          {
            content: 'Location:',
            children: [`United States: ${address}`]
          },
          {
            content: 'Age:',
            children: [`${adset.ageFrom}-${adset.ageTo}`]
          }
        ];

    geoLocations = {
      custom_locations: [
        {
          name: address,
          address_string: address,
          radius,
          distance_unit: 'mile'
        }
      ],
      location_types: ['home', 'recent']
    };
  }

  // detailed targeting
  // pass "interests" object in the targeting below.
  // Each interests element should have id and name from the targetingsearch response.

  const includeTargetingObject = parameters.includeTargetingSelected.find(
    (selection) => selection.adSetId === adset.id
  )
    ? parameters.includeTargetingSelected.find(
        (selection) => selection.adSetId === adset.id
      ).selected
    : [];
  const excludeTargetingObject = parameters.excludeTargetingSelected.find(
    (selection) => selection.adSetId === adset.id
  )
    ? parameters.excludeTargetingSelected.find(
        (selection) => selection.adSetId === adset.id
      ).selected
    : [];

  /**
   * End of targeting definition..
   */

  const optimizationGoal = {
    VIDEO_VIEWS: constants.LAUNCHER.THRUPLAY,
    CONVERSIONS: constants.LAUNCHER.OFFSITE_CONVERSIONS,
    LINK_CLICKS: adset.optimizationGoal // optimization goals for traffic ads: LANDING_PAGE_VIEWS, LINK_CLICKS
  };

  const adSetData = {
    campaignId: newCampaign,
    accountId,
    name: adset.adSetName.replace(constants.LAUNCHER.CLIENT_NAME, accountName),
    start_time: startTime,
    end_time: endTime,
    status: constants.LAUNCHER.ACTIVE,
    optimization_goal:
      optimizationGoal[campaign.objective] ||
      constants.LAUNCHER.OFFSITE_CONVERSIONS,
    targetingsentencelines,
    targeting: {
      age_max: adset.ageTo,
      age_min: adset.ageFrom,
      custom_audiences: customAudiences,
      excluded_custom_audiences: exclusions,
      geo_locations: geoLocations,
      excluded_geo_locations: excludedGeoLocations,
      genders: gender,
      publisher_platforms: positions,
      device_platforms: devices,

      // below flexible_spec and exclusions are for the detailed targeting
      flexible_spec: [
        {
          interests: includeTargetingObject
        }
      ],
      exclusions: {
        interests: excludeTargetingObject
      }
    },
    attribution_spec: ![
      constants.LAUNCHER.VIDEO_VIEWS,
      constants.LAUNCHER.LINK_CLICKS
    ].includes(campaign.objective)
      ? [
          { event_type: constants.LAUNCHER.CLICK_THROUGH, window_days: 7 },
          { event_type: constants.LAUNCHER.VIEW_THROUGH, window_days: 1 }
        ]
      : [{ event_type: constants.LAUNCHER.CLICK_THROUGH, window_days: 1 }]
  };

  if (isHecCampaign) {
    delete adSetData.targeting.age_min;
    delete adSetData.targeting.age_max;
    delete adSetData.targeting.genders;
  }

  setPositions(adSetData, positions);

  const promotedObject = {
    pixel_id: pixel,
    custom_event_type: constants.LAUNCHER.CONTENT_VIEW
  };

  if (adset.optimizationGoal.indexOf('DISPLAY LINK') !== -1) {
    promotedObject.custom_event_type = 'OTHER';
    promotedObject.pixel_rule = `{and:[{event:{eq:PageView}},{or:[{URL:{i_contains:${displayUrl}}}]}]}`;
  }

  adSetData.promoted_object = ![
    'THRUPLAY',
    'LINK_CLICKS',
    'LANDING_PAGE_VIEWS'
  ].includes(adSetData.optimization_goal)
    ? promotedObject
    : {};

  if (languages && languages.length > 0) {
    adSetData.targeting.locales = languages;
  }

  return adSetData;
};

export const returnCanvasAdObjectWithCanvasUrl = (
  ad,
  newAdSet,
  form,
  canvasUrl
) => {
  const {
    accountId,
    pixel,
    pageId,
    // displayUrl,
    accountName,
    status,
    utmProfile
  } = form;
  const { canvas: utmCanvas } = utmProfile || {};
  const { ad: innerAd, name: adName } = ad;
  const {
    asset_id: assetId,
    title: adTitle,
    message: adMessage,
    thumbnail: adThumbnail,
    utm: adUtm
  } = innerAd;
  const canvasAdObject = {
    adSetId: newAdSet,
    accountId,
    name: adName.replace(constants.LAUNCHER.CLIENT_NAME, accountName),
    status,
    creative: {
      object_story_spec: {
        page_id: pageId,
        video_data: {
          video_id: assetId,
          title: adTitle,
          message: adMessage,
          call_to_action: {
            type: constants.LAUNCHER.LEARN_MORE,
            value: {
              // link_caption: displayUrl,
              link: canvasUrl
            }
          },
          image_url: adThumbnail
        }
      },
      degrees_of_freedom_spec: {
        creative_features_spec: {
          standard_enhancements: {
            enroll_status: 'OPT_OUT'
          }
        }
      },
      url_tags: utmCanvas || adUtm
    },
    tracking_specs: [
      {
        'action.type': [constants.LAUNCHER.OFFSITE_CONVERSIONS_VALUE],
        fb_pixel: [pixel]
      }
    ],
    conversion_domain: returnConversionDomainUrl({
      destinationUrl: form.destinationUrl,
      campaign: form.willUseConversionDomain
        ? { objective: 'CONVERSIONS' }
        : null
    })
  };
  return canvasAdObject;
};

// Method to launch a Canvas Ad to Facebook
export const createCanvasAd = async (
  ad,
  campaign,
  newAdSet,
  adset,
  token,
  form
) => {
  const adData = await createCanvasAdObject(
    ad,
    campaign,
    newAdSet,
    adset,
    token,
    form
  );

  let resultAd = '';
  resultAd = await buildAd(adData, token);

  let attempts = 0;

  while (attempts < 5 && resultAd.error) {
    attempts = attempts + 1;
    // eslint-disable-next-line no-await-in-loop
    resultAd = await buildAd(adData, token);
  }
  resultAd.name = adData.name;

  return resultAd;
};

// Method to create the object to launch a Canvas Ad to Facebook
export const createCanvasAdObject = async (
  ad,
  campaign,
  newAdSet,
  adset,
  token,
  form
) => {
  const {
    accountId,
    pixel,
    pageId,
    displayUrl,
    ads,
    accountName,
    status,
    utmProfile
  } = form;

  let destinationUrl = '';
  const adFields = ads.find((item) => item.id === parseInt(ad.id, 10));
  if (adFields) {
    destinationUrl = adFields.destinationUrl;
  }

  const canvasUrl = await generateCanvasUrl(
    ad.ad.resource_id,
    destinationUrl,
    form,
    token
  );

  return {
    adSetId: newAdSet,
    accountId,
    name: ad.name.replace(constants.LAUNCHER.CLIENT_NAME, accountName),
    status,
    creative: {
      object_story_spec: {
        page_id: pageId,
        video_data: {
          video_id: ad.ad.asset_id,
          title: ad.ad.title,
          message: ad.ad.message,
          call_to_action: {
            type: constants.LAUNCHER.LEARN_MORE,
            value: {
              link_caption: displayUrl,
              link: canvasUrl
            }
          },
          image_url: ad.ad.thumbnail
        }
      },
      degrees_of_freedom_spec: {
        creative_features_spec: {
          standard_enhancements: {
            enroll_status: 'OPT_OUT'
          }
        }
      },
      url_tags: utmProfile && utmProfile.canvas ? utmProfile.canvas : ad.ad.utm
    },
    tracking_specs: [
      {
        'action.type': [constants.LAUNCHER.OFFSITE_CONVERSIONS_VALUE],
        fb_pixel: [pixel]
      }
    ],
    conversion_domain: returnConversionDomainUrl({ destinationUrl, campaign })
  };
};

// Method to launch a canvas to a page and retrieve the url to create a Canvas Ad.
export const generateCanvasUrl = async (resourceId, destinationUrl, form, token) => {
  const { accountId, pageId, pageAccessToken } = form;
  const resultCanvas = await getCanvas(resourceId);

  const formatedCanvas = formatCanvasToLaunch(resultCanvas.canvas);
  const lastElement = _.maxBy(formatedCanvas.canvasElements, (element) => {
    return element.order;
  });

  if (lastElement && lastElement.type === constants.LAUNCHER.PHOTO) {
    lastElement.imageTargetUrl = destinationUrl;
  }

  const parameters = {
    accountId,
    pageId,
    is_published: true,
    token: pageAccessToken,
    accessToken: token,
    canvasId: resourceId
  };
  const resultLaunchCanvas = await launchCanvasToFacebook(
    parameters,
    formatedCanvas
  );
  const canvasData = await getCanvasFromFacebook(
    resultLaunchCanvas.result,
    pageAccessToken
  );

  return canvasData.canvas_link;
};

// Method to launch a Collection Ad to Facebook
export const createCollectionAd = async (
  ad,
  campaign,
  newAdSet,
  adset,
  form,
  token
) => {
  const {
    ads,
    accountId,
    pixel,
    pageId,
    displayUrl,
    pageAccessToken,
    accountName,
    description,
    status,
    utmProfile
  } = form;

  let adFields = ads.find((item) => item.id === ad.id);

  if (!adFields) {
    const adsetType = ad.name.split('_')[4];
    adFields = ads.find((item) => {
      switch (adsetType) {
        case 'Lookalike':
          return ad.id === item.id || ad.id === item.id * 2 + 1;
        case 'Retargeting':
          return ad.id === item.id || ad.id === item.id * 2;
        default:
          return ad.id === item.id;
      }
    });
  }

  const splitAdsetName = adset.adSetName.split('_');
  const splitAdName = ad.name.split('_');
  const adsetVariant = splitAdsetName[4]; // ex: In Market, Lookalike, Retargeting
  const adVariant = splitAdName[4]; // ex: In Market, Lookalike, Retargeting

  // On relaunches, sometimes lookalike and retargeting duplicates are not named properly
  if (
    (adsetVariant === 'Lookalike' || adsetVariant === 'Retargeting') &&
    adVariant !== adsetVariant
  ) {
    // eslint-disable-next-line no-param-reassign
    ad.name = splitAdName
      .slice(0, 4)
      .concat(adsetVariant)
      .concat(splitAdName.slice(5));
  }

  const parameters = {
    adSetId: newAdSet,
    accountId,
    name: ad.name.replace(constants.LAUNCHER.CLIENT_NAME, accountName),
    status,
    productSet: adFields.productSet,
    pageId,
    videoId: ad.ad.asset_id,
    title: ad.ad.title,
    message: ad.ad.message,
    displayLink: displayUrl,
    imageUrl: ad.ad.thumbnail,
    urlTags:
      utmProfile && utmProfile.collection ? utmProfile.collection : ad.ad.utm,
    pixel,
    token,
    pageToken: pageAccessToken,
    description,
    videoUrl: ad.ad.video,
    destinationUrl: adFields.footerUrl,
    buttonText: ad.ad.footer_text,
    conversionDomain: returnConversionDomainUrl({
      destinationUrl: adFields && adFields.footerUrl ? adFields.footerUrl : '',
      campaign
    })
  };

  const resultAd = await createCollection(parameters);
  resultAd.name = parameters.name;

  return resultAd;
};

// Method to launch a Carousel Ad to Facebook in 2 steps. The first step creates the creative and the second step launches the Carousel Ad to Facebook using the created creative
export const createBreakdownCarouselAd = async (
  ad,
  campaign,
  newAdSet,
  adset,
  form,
  token
) => {
  const {
    ads,
    accountId,
    pixel,
    pageId,
    displayUrl,
    accountName,
    instagramAccount,
    status,
    utmProfile,
    formCardThumbnails
  } = form;

  const adFields = ads.find((item) => item.id === parseInt(ad.id, 10));
  const cards = [];

  ad.ad.cards.forEach((card) => {
    let cardFields = null;
    let foundCardThumbnail = card.thumbnail;
    if (adFields) {
      cardFields = adFields.cards.find((item) => item.cardId === card.id);
      if (formCardThumbnails) {
        foundCardThumbnail = formCardThumbnails.find((formCard) => {
          return formCard.id === cardFields.cardId;
        }).thumbnail;
      }
    }
    cards.push({
      link: cardFields ? cardFields.destinationUrl : '',
      picture: foundCardThumbnail,
      name: card.headline,
      description: card.description,
      call_to_action: {
        type: ad.ad.call_to_action,
        value: { link: cardFields ? cardFields.destinationUrl : '' }
      },
      video_id: card.asset_id
    });
  });

  const creativeParameters = {
    name: 'Creative',
    // enroll_status: 'OPT_OUT',
    degrees_of_freedom_spec:{
      creative_features_spec: {
        standard_enhancements: {
          enroll_status: 'OPT_OUT'
        }
      }
    },
    object_story_spec: {
      page_id: pageId,
      link_data: {
        link: displayUrl,
        message: ad.ad.message,
        caption: displayUrl,
        call_to_action: { type: ad.ad.call_to_action },
        child_attachments: cards,
        multi_share_end_card: false,
        multi_share_optimized: false
      }
    },
    url_tags:
      utmProfile && utmProfile.carousel ? utmProfile.carousel : ad.ad.utm,
    instagram_actor_id: instagramAccount,
    access_token: token
  };

  const resultCreative = await createCreative(accountId, creativeParameters);
  const { id: resultCreativeId } = resultCreative;

  let resultAd = {};

  if (!resultCreativeId) {
    return {
      error: {
        error_user_title: 'Ad Creative Error',
        error_user_msg:
          'Ad creative generation failed. Check the carousel card thumbnails and other content or remove some cards, and then try again'
      }
    };
  }

  const adData = {
    adSetId: newAdSet,
    accountId,
    name: ad.name.replace(constants.LAUNCHER.CLIENT_NAME, accountName),
    status,
    creative: { creative_id: resultCreativeId },
    tracking_specs: [
      {
        'action.type': [constants.LAUNCHER.OFFSITE_CONVERSIONS_VALUE],
        fb_pixel: [pixel]
      }
    ],
    // in carousel ads, conversion_domain should use the first card's destination url
    conversion_domain: returnConversionDomainUrl({
      destinationUrl:
        adFields.cards && adFields.cards.length
          ? adFields.cards[0].destinationUrl
          : '',
      campaign
    })
  };

  resultAd = await buildAd(adData, token);

  let attempts = 0;

  while (attempts < 5 && resultAd.error) {
    attempts = attempts + 1;
    // eslint-disable-next-line no-await-in-loop
    resultAd = await buildAd(adData, token);
  }
  resultAd.name = adData.name;

  return resultAd;
};

// Method to launch a Carousel Ad to Facebook
export const createCarouselAd = async (
  ad,
  campaign,
  newAdSet,
  adset,
  form,
  token
) => {
  const {
    ads,
    accountId,
    pixel,
    pageId,
    displayUrl,
    accountName,
    utmProfile
  } = form;
  const adFields = ads.find((item) => item.id === parseInt(ad.id, 10));

  const cards = [];

  ad.ad.cards.forEach((card) => {
    let cardFields = null;
    if (adFields) {
      cardFields = adFields.cards.find((item) => item.cardId === card.id);
    }

    cards.push({
      link: cardFields ? cardFields.destinationUrl : '',
      picture: card.thumbnail,
      name: card.headline,
      description: card.description,
      call_to_action: {
        type: ad.ad.call_to_action,
        value: { link: cardFields ? cardFields.destinationUrl : '' }
      },
      video_id: card.asset_id
    });
  });

  const adData = {
    adSetId: newAdSet,
    accountId,
    name: ad.name.replace(constants.LAUNCHER.CLIENT_NAME, accountName),
    status: constants.LAUNCHER.ACTIVE,
    creative: {
      object_story_spec: {
        page_id: pageId,
        link_data: {
          link: displayUrl,
          message: ad.ad.message,
          caption: displayUrl,
          call_to_action: { type: ad.ad.call_to_action },
          child_attachments: cards,
          multi_share_end_card: false,
          multi_share_optimized: false
        }
      },
      degrees_of_freedom_spec: {
        creative_features_spec: {
          standard_enhancements: {
            enroll_status: 'OPT_OUT'
          }
        }
      },
      url_tags:
        utmProfile && utmProfile.carousel ? utmProfile.carousel : ad.ad.utm
    },
    tracking_specs: [
      {
        'action.type': [constants.LAUNCHER.OFFSITE_CONVERSIONS_VALUE],
        fb_pixel: [pixel]
      }
    ]
  };

  let resultAd = '';
  resultAd = await buildAd(adData, token);

  let attempts = 0;

  while (attempts < 5 && resultAd.error) {
    attempts = attempts + 1;
    // eslint-disable-next-line no-await-in-loop
    resultAd = await buildAd(adData, token);
  }
  resultAd.name = adData.name;

  return resultAd;
};

// Method to launch a DAA Ad to Facebook
export const createDAAAd = async (
  ad,
  campaign,
  newAdSet,
  adset,
  form,
  token
) => {
  const {
    accountId,
    pixel,
    pageId,
    displayUrl,
    accountName,
    adsets,
    status,
    utmProfile,
    destinationUrl
  } = form;
  const { LAUNCHER } = constants;
  const {
    CLIENT_NAME,
    PRODUCT_CATALOG_SALES,
    OFFSITE_CONVERSIONS_VALUE,
    STILL
  } = LAUNCHER;
  const adsetParameters = adsets.find((item) => item.id === adset.id);
  const { productSet } = adsetParameters || {};
  const { name, ad: innerAd } = ad;
  const {
    message,
    title,
    footer_text: description, // footer_text is used as the description (News Link Desc) in DAA Ads
    call_to_action: callToAction,
    utm,
    asset_id: assetId
  } = innerAd;

  const adData = {
    adSetId: newAdSet,
    accountId,
    name: name.replace(CLIENT_NAME, accountName),
    status,
    objective: PRODUCT_CATALOG_SALES,
    productSet,
    creative: {
      object_story_spec: {
        page_id: pageId,
        template_data: {
          link: displayUrl,
          message,
          name: title,
          description: description || '',
          call_to_action: {
            type: callToAction
          }
        }
      },
      degrees_of_freedom_spec: {
        creative_features_spec: {
          standard_enhancements: {
            enroll_status: 'OPT_OUT'
          }
        }
      },
      url_tags: utm
    },
    tracking_specs: [
      {
        'action.type': [OFFSITE_CONVERSIONS_VALUE],
        fb_pixel: [pixel]
      }
    ],
    conversion_domain: returnConversionDomainUrl({
      campaign,
      destinationUrl: destinationUrl || displayUrl
    })
  };

  if (assetId === STILL) {
    adData.creative.object_story_spec.template_data.multi_share_end_card = true;
    adData.creative.object_story_spec.template_data.show_multiple_images = false;
    adData.creative.object_story_spec.template_data.force_single_link = true;
    adData.creative.url_tags =
      utmProfile && utmProfile.daa_still
        ? utmProfile.daa_still
        : adData.creative.url_tags;
  } else {
    adData.creative.object_story_spec.template_data.multi_share_end_card = true;
    adData.creative.object_story_spec.template_data.format_option =
      'carousel_images_multi_items';
    adData.creative.url_tags =
      utmProfile && utmProfile.daa_carousel
        ? utmProfile.daa_carousel
        : adData.creative.url_tags;
  }

  let resultAd = '';
  resultAd = await buildAd(adData, token);

  let attempts = 0;

  while (attempts < 5 && resultAd.error) {
    attempts = attempts + 1;
    // eslint-disable-next-line no-await-in-loop
    resultAd = await buildAd(adData, token);
  }
  resultAd.name = adData.name;

  return resultAd;
};

// Method to launch a Still Ad to Facebook
export const createStillAd = async (
  ad,
  campaign,
  newAdSetId,
  adset,
  token,
  form
) => {
  const adData = await createStillAdObject(
    ad,
    campaign,
    newAdSetId,
    adset,
    form
  );

  let resultAd = '';
  resultAd = await buildAd(adData, token);

  let attempts = 0;

  while (attempts < 5 && resultAd.error) {
    attempts = attempts + 1;
    // eslint-disable-next-line no-await-in-loop
    resultAd = await buildAd(adData, token);
  }
  resultAd.name = adData.name;

  return resultAd;
};

// Method to create the object to launch a Still Ad to Facebook
export const createStillAdObject = async (
  ad,
  campaign,
  newAdSetId,
  adset,
  form
) => {
  const {
    accountId,
    pageId,
    ads,
    accountName,
    status,
    utmProfile,
    pixel
  } = form;
  const { ad: innerAd } = ad;
  const {
    thumbnail: adImageUrl,
    title,
    message,
    call_to_action,
    footer_text
  } = innerAd;

  let destinationUrl = '';
  const adFields = ads.find((item) => item.id === parseInt(ad.id, 10));
  if (adFields) {
    destinationUrl = adFields.destinationUrl;
  }

  const stillAdObject = {
    adSetId: newAdSetId,
    accountId,
    status,
    name: ad.name.replace(constants.LAUNCHER.CLIENT_NAME, accountName),
    creative: {
      object_story_spec: {
        page_id: pageId,
        link_data: {
          picture: adImageUrl,
          link: destinationUrl, // this field is required
          message, // message on top of the ad image
          name: title, // the title of the ad
          description: footer_text,
          call_to_action: {
            type: call_to_action
            // value: {
            //   link_caption: displayUrl,
            //   link: destinationUrl
            // }
          }
        }
      },
      degrees_of_freedom_spec: {
        creative_features_spec: {
          standard_enhancements: {
            enroll_status: 'OPT_OUT'
          }
        }
      },
      url_tags: utmProfile && utmProfile.video ? utmProfile.video : ad.ad.utm
    },
    tracking_specs: [
      {
        'action.type': [constants.LAUNCHER.OFFSITE_CONVERSIONS_VALUE],
        fb_pixel: [pixel]
      }
    ],
    conversion_domain: returnConversionDomainUrl({
      destinationUrl,
      campaign
    })
  };

  return stillAdObject;
};



// WEBPACK FOOTER //
// ./src/screens/PackageBuilder/PackageLauncher.js